import { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { HardHat, ShieldCheck, CheckCircle2, Users, Cog } from "lucide-react";
import { useLang } from "@/contexts/LanguageContext";
import { IMAGES, SITE } from "@/config/site";
import Section, { RevealItem } from "@/components/Section";
import FloatingObjects from "@/components/FloatingObjects";
import PageHead from "@/components/PageHead";

gsap.registerPlugin(ScrollTrigger);

export default function Manpower() {
  const { t, lang } = useLang();
  const statsRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      const counters = statsRef.current?.querySelectorAll("[data-counter]") || [];
      counters.forEach((el) => {
        const end = parseInt(el.dataset.counter, 10);
        gsap.fromTo(
          el,
          { innerText: 0 },
          {
            innerText: end,
            duration: 1.4,
            ease: "power2.out",
            snap: { innerText: 1 },
            scrollTrigger: { trigger: el, start: "top 88%" },
            onUpdate() {
              el.innerText = Math.floor(this.targets()[0].innerText).toString();
            },
          }
        );
      });
    });
    return () => ctx.revert();
  }, []);

  const total = t.manpower.stats.reduce((sum, s) => sum + s.value, 0);

  return (
    <>
      <PageHead customTitle={`${t.manpower.title} · ${SITE.brand.en}`} customDesc={t.manpower.subtitle} />

      <section className="relative pt-40 pb-12 overflow-hidden" data-testid="manpower-hero">
        <div className="absolute inset-0 -z-10">
          <img src={IMAGES.manpower} alt="" className="w-full h-full object-cover opacity-25" loading="lazy" />
          <div className="absolute inset-0 bg-gradient-to-b from-navy-900 via-navy-900/85 to-navy-900" />
          <FloatingObjects density="low" />
          {/* Big rotating gear */}
          <div className="absolute -bottom-32 -right-32 opacity-10">
            <Cog className="w-[460px] h-[460px] text-gold-500 animate-gear-slow" strokeWidth={0.4} />
          </div>
        </div>
        <div className="container-pad">
          <div className="max-w-3xl">
            <div className="text-overline mb-4 flex items-center gap-2">
              <Users className="w-3.5 h-3.5 text-gold-500" /> {t.manpower.overline}
            </div>
            <h1 className="font-heading font-black tracking-tighter text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.05]">
              {t.manpower.title}
            </h1>
            <p className="mt-5 text-white/70 text-base md:text-lg max-w-2xl leading-relaxed">
              {t.manpower.subtitle}
            </p>
          </div>
        </div>
      </section>

      {/* Stats Grid */}
      <Section testId="manpower-stats" className="!py-16">
        <div className="container-pad">
          <div ref={statsRef} className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
            {t.manpower.stats.map((s, i) => (
              <RevealItem
                key={i}
                className="metallic-border-top relative p-6 bg-gradient-to-br from-steel-700/40 via-navy-800 to-navy-900 border border-white/10 hover:border-gold-500/40 transition-colors group"
              >
                <div className="font-heading font-black text-4xl md:text-5xl text-gold-shine">
                  <span data-counter={s.value}>0</span>
                </div>
                <div className="mt-2 text-xs md:text-sm text-white/70 tracking-wider leading-snug">
                  {s.label}
                </div>
                <div className="absolute bottom-3 right-3 font-mono text-[10px] text-white/35 tracking-widest">
                  EAH · {String(i + 1).padStart(2, "0")}
                </div>
              </RevealItem>
            ))}

            <RevealItem className="metallic-border-top relative p-6 bg-maroon-grad border border-maroon-400/40 col-span-2 sm:col-span-3 lg:col-span-4 flex items-center justify-between gap-4 flex-wrap">
              <div>
                <div className="text-overline !text-white/70">
                  {lang === "ar" ? "إجمالي الكوادر" : "Total Workforce"}
                </div>
                <div className="font-heading font-black text-5xl md:text-6xl text-white">
                  {total}+
                </div>
              </div>
              <div className="text-white/80 text-sm md:text-base max-w-md">
                {lang === "ar"
                  ? "فريق متعدد التخصصات يعمل بانضباط ومنهجية واضحة على المواقع الصناعية في المملكة."
                  : "A multi-disciplinary team operating with discipline and clear methodology across industrial sites in KSA."}
              </div>
            </RevealItem>
          </div>
        </div>
      </Section>

      {/* Safety */}
      <Section testId="manpower-safety" className="bg-navy-900/60">
        <div className="absolute inset-0 -z-10 grid-blueprint opacity-30" />
        <div className="container-pad grid lg:grid-cols-12 gap-12">
          <RevealItem className="lg:col-span-5">
            <div className="text-overline mb-3 flex items-center gap-2">
              <ShieldCheck className="w-3.5 h-3.5 text-gold-500" /> {t.manpower.safetyOverline}
            </div>
            <h2 className="font-heading font-black tracking-tight text-3xl md:text-4xl lg:text-5xl text-white leading-tight">
              {t.manpower.safetyTitle}
            </h2>
            <p className="mt-5 text-white/70 leading-relaxed">{t.manpower.safetyBody}</p>

            <div className="mt-8 relative w-40 h-40 md:w-52 md:h-52">
              {/* Floating PPE icon */}
              <div className="absolute inset-0 grid place-items-center animate-float">
                <div className="w-full h-full bg-maroon-600/20 border border-gold-500/40 flex items-center justify-center backdrop-blur-md">
                  <HardHat className="w-20 h-20 md:w-28 md:h-28 text-gold-shine" strokeWidth={1.2} />
                </div>
              </div>
              <div className="absolute -inset-4 -z-10 border border-white/5" />
            </div>
          </RevealItem>

          <RevealItem className="lg:col-span-7">
            <div className="space-y-3">
              {t.manpower.safetyChecks.map((c, i) => (
                <div
                  key={i}
                  data-testid={`safety-check-${i}`}
                  className="flex items-start gap-4 p-5 glass hover:border-gold-500/40 transition-colors"
                >
                  <CheckCircle2 className="w-5 h-5 mt-0.5 text-gold-500 shrink-0" strokeWidth={1.5} />
                  <div className="text-white/80 text-sm md:text-base leading-relaxed">{c}</div>
                </div>
              ))}
            </div>
          </RevealItem>
        </div>
      </Section>
    </>
  );
}
